import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Divider,
  Button,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import MarkupMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/MarkupMAIQ';
import { AttachmentInputMAIQ, AttachmentFile, AllowedFileFormat } from 'app/components/ReusableUIComponents/HighLevelComponents/AttachmentInputMAIQ';

// Component configurations
interface ComponentConfig {
  name: string;
  path: string;
  props: PropConfig[];
  // Optional: Custom component renderer function
  // If provided, this will be used instead of the default renderer
  componentRenderer?: (props: Record<string, any>, additionalState?: Record<string, any>) => React.ReactElement;
  // Optional: Additional state needed for the component (e.g., attachments for AttachmentInputMAIQ)
  additionalStateConfig?: {
    key: string;
    initialValue: any;
    stateSetter?: (value: any) => void;
  };
}

interface PropConfig {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'enum' | 'array' | 'object';
  defaultValue: any;
  enumValues?: string[];
  description?: string;
  // Optional: Custom renderer for this prop
  customRenderer?: (props: {
    value: any;
    onChange: (value: any) => void;
    prop: PropConfig;
  }) => React.ReactElement;
  // For array type: specify if items are enum values
  arrayItemType?: 'enum';
  arrayItemEnum?: any; // Enum object (e.g., AllowedFileFormat)
  // For string type: specify if multiline
  multiline?: boolean;
  rows?: number;
  // Hide this prop from the UI (useful for internal state management)
  hidden?: boolean;
}

const COMPONENT_CONFIGS: ComponentConfig[] = [
  {
    name: 'MarkupMAIQ',
    path: 'app/components/ReusableUIComponents/HighLevelComponents/MarkupMAIQ',
    props: [
      {
        name: 'content',
        type: 'string',
        defaultValue: '',
        description: 'Markdown content to render',
        multiline: true,
        rows: 6,
      },
    ],
    componentRenderer: (props) => (
      <Box>
        {props.content ? (
          <MarkupMAIQ content={props.content} />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Enter content above to see the rendered output
          </Typography>
        )}
      </Box>
    ),
  },
  {
    name: 'AttachmentInputMAIQ',
    path: 'app/components/ReusableUIComponents/HighLevelComponents/AttachmentInputMAIQ',
    props: [
      {
        name: 'disabled',
        type: 'boolean',
        defaultValue: false,
        description: 'Disable the component',
      },
      {
        name: 'maxAttachments',
        type: 'number',
        defaultValue: 5,
        description: 'Maximum number of attachments allowed',
      },
      {
        name: 'allowedFileFormats',
        type: 'array',
        defaultValue: [
          AllowedFileFormat.IMAGE_JPEG,
          AllowedFileFormat.IMAGE_JPG,
          AllowedFileFormat.IMAGE_PNG,
          AllowedFileFormat.IMAGE_GIF,
          AllowedFileFormat.IMAGE_WEBP,
          AllowedFileFormat.PDF,
        ],
        description: 'Array of allowed file formats',
        arrayItemType: 'enum',
        arrayItemEnum: AllowedFileFormat,
      },
      {
        name: 'maxFileSize',
        type: 'number',
        defaultValue: 2 * 1024 * 1024, // 2MB
        description: 'Maximum file size in bytes',
      },
      {
        name: 'showGuidelines',
        type: 'boolean',
        defaultValue: true,
        description: 'Show file attachment guidelines',
      },
      {
        name: 'highlightedFormat',
        type: 'string',
        defaultValue: 'PDF',
        description: 'Format to highlight (e.g., PDF, JPEG, PNG)',
      },
    ],
    additionalStateConfig: {
      key: 'attachments',
      initialValue: [] as AttachmentFile[],
    },
    componentRenderer: (props, additionalState) => (
      <AttachmentInputMAIQ
        attachments={additionalState?.attachments || []}
        onAttachmentsChange={additionalState?.setAttachments || (() => {})}
        disabled={props.disabled}
        maxAttachments={props.maxAttachments}
        allowedFileFormats={props.allowedFileFormats}
        maxFileSize={props.maxFileSize}
        showGuidelines={props.showGuidelines}
        guidelinesTitle={props.guidelinesTitle}
        highlightedFormat={props.highlightedFormat}
      />
    ),
  },
];

export const ComponentsTest: React.FC = () => {
  const [expandedComponent, setExpandedComponent] = useState<string | null>(null);
  const [componentProps, setComponentProps] = useState<Record<string, Record<string, any>>>(() => {
    const initial: Record<string, Record<string, any>> = {};
    COMPONENT_CONFIGS.forEach((config) => {
      initial[config.name] = {};
      config.props.forEach((prop) => {
        if (!prop.hidden) {
          initial[config.name][prop.name] = prop.defaultValue;
        }
      });
    });
    return initial;
  });
  
  // Generic additional state management for components
  const [additionalState, setAdditionalState] = useState<Record<string, Record<string, any>>>(() => {
    const initial: Record<string, Record<string, any>> = {};
    COMPONENT_CONFIGS.forEach((config) => {
      if (config.additionalStateConfig) {
        initial[config.name] = {
          [config.additionalStateConfig.key]: config.additionalStateConfig.initialValue,
        };
      }
    });
    return initial;
  });

  const handlePropChange = (componentName: string, propName: string, value: any) => {
    setComponentProps((prev) => ({
      ...prev,
      [componentName]: {
        ...prev[componentName],
        [propName]: value,
      },
    }));
  };

  const handleToggleExpand = (componentName: string) => {
    setExpandedComponent((prev) => (prev === componentName ? null : componentName));
  };

  const renderPropEditor = (componentName: string, prop: PropConfig) => {
    // If prop has custom renderer, use it
    if (prop.customRenderer) {
      return prop.customRenderer({
        value: componentProps[componentName]?.[prop.name] ?? prop.defaultValue,
        onChange: (value) => handlePropChange(componentName, prop.name, value),
        prop,
      });
    }

    const currentValue = componentProps[componentName]?.[prop.name] ?? prop.defaultValue;

    switch (prop.type) {
      case 'string':
        return (
          <Box>
            <Chip
              label={`${prop.name} (string)`}
              size="small"
              sx={{
                mb: 1,
                height: 20,
                fontSize: '0.65rem',
                fontFamily: 'monospace',
                fontWeight: 500,
              }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder={prop.name}
              value={currentValue}
              onChange={(e) => handlePropChange(componentName, prop.name, e.target.value)}
              multiline={prop.multiline}
              rows={prop.rows || 1}
            />
          </Box>
        );

      case 'number':
        return (
          <Box>
            <Chip
              label={`${prop.name} (number)`}
              size="small"
              sx={{
                mb: 1,
                height: 20,
                fontSize: '0.65rem',
                fontFamily: 'monospace',
                fontWeight: 500,
              }}
            />
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder={prop.name}
              value={currentValue}
              onChange={(e) => handlePropChange(componentName, prop.name, Number(e.target.value))}
            />
          </Box>
        );

      case 'boolean':
        return (
          <Box>
            <Chip
              label={`${prop.name} (boolean)`}
              size="small"
              sx={{
                mb: 1,
                height: 20,
                fontSize: '0.65rem',
                fontFamily: 'monospace',
                fontWeight: 500,
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentValue}
                  onChange={(e) => handlePropChange(componentName, prop.name, e.target.checked)}
                />
              }
              label={prop.name}
            />
          </Box>
        );

      case 'enum':
        return (
          <Box>
            <Chip
              label={`${prop.name} (enum)`}
              size="small"
              sx={{
                mb: 1,
                height: 20,
                fontSize: '0.65rem',
                fontFamily: 'monospace',
                fontWeight: 500,
              }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>{prop.name}</InputLabel>
              <Select
                value={currentValue}
                label={prop.name}
                onChange={(e) => handlePropChange(componentName, prop.name, e.target.value)}
              >
                {prop.enumValues?.map((val) => (
                  <MenuItem key={val} value={val}>
                    {val}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case 'array':
        // Generic enum array handling
        if (prop.arrayItemType === 'enum' && prop.arrayItemEnum) {
          const enumValues = Object.values(prop.arrayItemEnum);
          const selectedValues = (currentValue || []);
          
          return (
            <Box>
              <Chip
                label={`${prop.name} (array<enum>)`}
                size="small"
                sx={{
                  mb: 1,
                  height: 20,
                  fontSize: '0.65rem',
                  fontFamily: 'monospace',
                  fontWeight: 500,
                }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {enumValues.map((enumValue: any) => (
                  <Chip
                    key={enumValue}
                    label={enumValue}
                    color={selectedValues.includes(enumValue) ? 'primary' : 'default'}
                    onClick={() => {
                      const newValues = selectedValues.includes(enumValue)
                        ? selectedValues.filter((v: any) => v !== enumValue)
                        : [...selectedValues, enumValue];
                      handlePropChange(componentName, prop.name, newValues);
                    }}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          );
        }
        // Default array handling (JSON input)
        return (
          <Box>
            <Chip
              label={`${prop.name} (array)`}
              size="small"
              sx={{
                mb: 1,
                height: 20,
                fontSize: '0.65rem',
                fontFamily: 'monospace',
                fontWeight: 500,
              }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder={prop.name}
              value={JSON.stringify(currentValue)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  handlePropChange(componentName, prop.name, parsed);
                } catch {
                  // Invalid JSON, ignore
                }
              }}
            />
          </Box>
        );

      default:
        return (
          <Box>
            <Chip
              label={`${prop.name} (${prop.type})`}
              size="small"
              sx={{
                mb: 1,
                height: 20,
                fontSize: '0.65rem',
                fontFamily: 'monospace',
                fontWeight: 500,
              }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder={prop.name}
              value={JSON.stringify(currentValue)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  handlePropChange(componentName, prop.name, parsed);
                } catch {
                  // Invalid JSON, ignore
                }
              }}
            />
          </Box>
        );
    }
  };

  const renderComponent = (config: ComponentConfig) => {
    const props = componentProps[config.name] || {};
    const isExpanded = expandedComponent === config.name;

    // Prepare additional state for component renderer
    let additionalStateForRenderer: Record<string, any> = {};
    if (config.additionalStateConfig) {
      const stateKey = config.additionalStateConfig.key;
      additionalStateForRenderer[stateKey] = additionalState[config.name]?.[stateKey] || config.additionalStateConfig.initialValue;
      
      // Create setter function for additional state
      additionalStateForRenderer[`set${stateKey.charAt(0).toUpperCase() + stateKey.slice(1)}`] = (value: any) => {
        setAdditionalState((prev) => ({
          ...prev,
          [config.name]: {
            ...prev[config.name],
            [stateKey]: value,
          },
        }));
      };
    }

    // Filter out hidden props from display
    const visibleProps = config.props.filter((prop) => !prop.hidden);

    return (
      <Accordion
        expanded={isExpanded}
        onChange={() => handleToggleExpand(config.name)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <CodeIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              &lt;{config.name} /&gt;
            </Typography>
            <Chip
              label={config.path.split('/').pop()}
              size="small"
              sx={{ ml: 'auto', fontFamily: 'monospace', fontSize: '0.7rem' }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Props Editor */}
            <Card>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Props Configuration
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {visibleProps.map((prop) => (
                    <Box key={prop.name}>
                      {renderPropEditor(config.name, prop)}
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>

            <Divider />

            {/* Component Preview */}
            <Card>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Component Preview
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.paper',
                    minHeight: 200,
                    maxHeight: 400,
                    overflow: 'auto',
                  }}
                >
                  {config.componentRenderer ? (
                    config.componentRenderer(props, additionalStateForRenderer)
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No component renderer configured for {config.name}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        Components Test
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Click on a component to expand and test its props. Modify prop values to see live changes.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {COMPONENT_CONFIGS.map((config) => (
          <Box key={config.name}>
            {renderComponent(config)}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
